import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import QuickRequestModal from '../../../components/ui/QuickRequestModal';

const HeroSection = () => {
  const [searchFilters, setSearchFilters] = useState({
    commune: '',
    propertyType: '',
    priceRange: '',
    bedrooms: ''
  });

  const [quickRequestModal, setQuickRequestModal] = useState({
    isOpen: false,
    requestType: null
  });

  const communeOptions = [
    { value: '', label: 'Toutes les communes' },
    { value: 'cocody', label: 'Cocody' },
    { value: 'plateau', label: 'Plateau' },
    { value: 'marcory', label: 'Marcory' },
    { value: 'yopougon', label: 'Yopougon' },
    { value: 'adjame', label: 'Adjamé' },
    { value: 'treichville', label: 'Treichville' },
    { value: 'koumassi', label: 'Koumassi' },
    { value: 'port-bouet', label: 'Port-Bouët' },
    { value: 'abobo', label: 'Abobo' },
    { value: 'attécoubé', label: 'Attécoubé' }
  ];

  const propertyTypeOptions = [
    { value: '', label: 'Type de propriété' },
    { value: 'villa', label: 'Villa' },
    { value: 'appartement', label: 'Appartement' },
    { value: 'duplex', label: 'Duplex' },
    { value: 'studio', label: 'Studio' },
    { value: 'terrain', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const priceRangeOptions = [
    { value: '', label: 'Gamme de prix' },
    { value: '0-50000000', label: '0 – 50M XOF' },
    { value: '50000000-100000000', label: '50M – 100M XOF' },
    { value: '100000000-200000000', label: '100M – 200M XOF' },
    { value: '200000000-500000000', label: '200M – 500M XOF' },
    { value: '500000000+', label: '500M+ XOF' }
  ];

  const bedroomOptions = [
    { value: '', label: 'Chambres' },
    { value: '1', label: '1 chambre' },
    { value: '2', label: '2 chambres' },
    { value: '3', label: '3 chambres' },
    { value: '4', label: '4 chambres' },
    { value: '5+', label: '5+ chambres' }
  ];

  const handleFilterChange = (field, value) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuickRequest = (requestType) => {
    setQuickRequestModal({ isOpen: true, requestType });
  };

  const closeQuickRequestModal = () => {
    setQuickRequestModal({ isOpen: false, requestType: null });
  };

  return (
    <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden border-none outline-none">
      
      {/* 
        Image de fond et dégradés
        On garde l'image, mais on applique un dégradé très doux 
        pour que le texte ressorte parfaitement sans paraître sombre.
      */}
      <div className="absolute inset-0 z-0 border-none outline-none">
        <Image
          src="https://rwtsvvpklmosipnatufe.supabase.co/storage/v1/object/sign/communes%20images/5.jpeg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9lM2I2N2NhZi05ZjM3LTRkNWYtYTFkYi1mZWZkNWM4ZjYzZjgiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb21tdW5lcyBpbWFnZXMvNS5qcGVnIiwiaWF0IjoxNzc4NTcyNzc3LCJleHAiOjE4MTAxMDg3Nzd9.lfROEwByZ__qOCDEGmHUPAMgj_7ikIf66Qq4jaICAAs"
          alt="Vue panoramique d'Abidjan, Côte d'Ivoire"
          className="w-full h-full object-cover transform scale-105" // Léger zoom pour un effet plus immersif
        />
        {/* Le dégradé se fond en noir absolu vers le bas pour une transition parfaite avec la suite */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[#050505]" />
      </div>

      {/* Contenu Héro : Centré, aéré, majestueux */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 lg:px-8 text-center pt-24 pb-16 flex flex-col items-center">

        {/* Titre géant (Style Apple) avec un dégradé sur le texte */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-heading font-bold text-white mb-6 leading-tight tracking-tighter">
          Trouvez votre propriété idéale <br className="hidden sm:block" />
          <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-brand-gold via-yellow-200 to-brand-gold">
            au cœur d'Abidjan.
          </span>
        </h1>

        <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto mb-14 font-body leading-relaxed font-light">
          Visites vidéo immersives, Transaction sécurisée de A à Z, Système anti-fraude. L'écosystème de confiance.
        </p>

        {/* 
          Carte de recherche : Le fameux "Frosted Glass" (Verre dépoli)
          Aucune bordure blanche, juste un fond translucide et un flou intense (blur-3xl).
        */}
        <div className="w-full max-w-4xl mx-auto bg-black/30 backdrop-blur-3xl rounded-3xl p-6 sm:p-8 shadow-2xl">
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* 
              Note : Vos composants Select devront idéalement avoir un style interne 
              épuré (sans grosses bordures), mais le fond de la carte fera déjà le gros du travail.
            */}
            <Select
              placeholder="Commune"
              options={communeOptions}
              value={searchFilters?.commune}
              onChange={(value) => handleFilterChange('commune', value)}
              className="w-full" 
            />
            <Select
              placeholder="Type"
              options={propertyTypeOptions}
              value={searchFilters?.propertyType}
              onChange={(value) => handleFilterChange('propertyType', value)}
              className="w-full" 
            />
            <Select
              placeholder="Prix"
              options={priceRangeOptions}
              value={searchFilters?.priceRange}
              onChange={(value) => handleFilterChange('priceRange', value)}
              className="w-full" 
            />
            <Select
              placeholder="Chambres"
              options={bedroomOptions}
              value={searchFilters?.bedrooms}
              onChange={(value) => handleFilterChange('bedrooms', value)}
              className="w-full" 
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/property-search-listings" className="w-full sm:w-auto">
              <Button 
                variant="default" 
                size="lg" 
                iconName="Search" 
                iconPosition="left" 
                className="w-full bg-white text-black hover:bg-gray-100 rounded-xl font-semibold shadow-lg transition-transform hover:scale-105 active:scale-95 border-none"
              >
                Rechercher des Propriétés
              </Button>
            </Link>
            <Link to="/commune-explorer" className="w-full sm:w-auto">
              <Button 
                variant="outline" 
                size="lg" 
                iconName="MapPin" 
                iconPosition="left" 
                className="w-full bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium backdrop-blur-md border-none transition-transform hover:scale-105 active:scale-95"
              >
                Explorer par Commune
              </Button>
            </Link>
          </div>

          {/* Séparateur quasi-invisible et boutons rapides */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6 pt-6 border-t border-white/5">
            <Button
              variant="secondary"
              size="default"
              iconName="Play"
              iconPosition="left"
              onClick={() => handleQuickRequest('visite')}
              className="w-full sm:w-auto bg-transparent hover:bg-white/5 text-white/70 hover:text-white border-none rounded-lg"
            >
              Demande de Visite
            </Button>
            <Button
              variant="secondary"
              size="default"
              iconName="Info"
              iconPosition="left"
              onClick={() => handleQuickRequest('info')}
              className="w-full sm:w-auto bg-transparent hover:bg-white/5 text-white/70 hover:text-white border-none rounded-lg"
            >
              Demande d'Informations
            </Button>
          </div>
        </div>

        {/* 
          Bandeau de Statistiques 
          Flottant et minimaliste, sans bordures.
        */}
        <div className="flex flex-wrap justify-center gap-8 sm:gap-16 mt-16 max-w-3xl mx-auto">
          {[
            { value: '2 500+', label: 'Propriétés Exclusives' },
            { value: '850+', label: 'Transactions Sécurisées' },
            { value: '10', label: 'Communes Couvertes' }
          ]?.map((stat) => (
            <div key={stat?.label} className="text-center flex flex-col items-center">
              <div className="text-3xl sm:text-4xl font-heading font-bold text-white tracking-tight mb-1">
                {stat?.value}
              </div>
              <div className="text-brand-gold/80 font-body text-xs sm:text-sm font-medium uppercase tracking-wider">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Indicateur de défilement très subtil */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce opacity-50">
        <Icon name="ChevronDown" size={28} color="white" strokeWidth={1} />
      </div>

      <QuickRequestModal
        isOpen={quickRequestModal?.isOpen}
        onClose={closeQuickRequestModal}
        requestType={quickRequestModal?.requestType} 
      />

    </section>
  );
};

export default HeroSection;